// Russian (RU) version of lvgimob.md — /ru/* URLs.
//
// SEO-separate from the Romanian version: own titles, h1, meta descriptions,
// canonical URLs and hreflang alternates. Translations live in the listing
// ru_* fields (editable in the admin); this file holds UI labels and texts.

import { SITE_URL, breadcrumbSchema, ORG_ID } from './jsonLd';

// RO base <-> RU base.
export const RU_BASE_MAP = {
  apartamente: 'kvartiry',
  case: 'doma',
  terenuri: 'uchastki',
  'spatii-comerciale': 'kommercheskaya-nedvizhimost',
  garajuri: 'garazhi',
  altele: 'prochee',
};

export const RO_BASE_MAP = Object.fromEntries(
  Object.entries(RU_BASE_MAP).map(([ro, ru]) => [ru, ro])
);

// RU categories served in Faza 1 (Ungheni) — label + API endpoint.
export const RU_CATEGORIES = [
  { ru: 'kvartiry', ro: 'apartamente', label: 'Квартиры', api: 'apartments' },
  { ru: 'doma', ro: 'case', label: 'Дома', api: 'houses' },
  { ru: 'uchastki', ro: 'terenuri', label: 'Участки', api: 'lands' },
  {
    ru: 'kommercheskaya-nedvizhimost',
    ro: 'spatii-comerciale',
    label: 'Коммерческая недвижимость',
    api: 'commercial-spaces',
  },
  { ru: 'garazhi', ro: 'garajuri', label: 'Гаражи', api: 'garajuri' },
  { ru: 'prochee', ro: 'altele', label: 'Прочее', api: 'altele' },
];

export const RU_ACTION = { 1: 'на продажу', 2: 'в аренду' };
export const RU_SALE_SUFFIX = { 1: 'prodazha', 2: 'arenda' };

export const CITY_RU = {
  ungheni: { name: 'Унгены', loc: 'Унгенах' },
  chisinau: { name: 'Кишинёв', loc: 'Кишинёве' },
  balti: { name: 'Бельцы', loc: 'Бельцах' },
};

export const ruCityName = (slug) => CITY_RU[slug]?.name || '';
export const ruCityLoc = (slug) => CITY_RU[slug]?.loc || 'Молдове';

export const ruListPath = (ruBase, locSlug, saleType) =>
  `/ru/${ruBase}${locSlug ? `/${locSlug}` : ''}/${RU_SALE_SUFFIX[Number(saleType) === 2 ? 2 : 1]}`;

export const ruDetailPath = (ruBase, locSlug, ruSlug) =>
  `/ru/${ruBase}/${locSlug}/${ruSlug}`;

export const ruUrl = (path) => `${SITE_URL}${path}`;

// Russian plural declension: 1 комната, 2 комнаты, 5 комнат.
export const ruPlural = (n, one, few, many) => {
  const num = Number(n);
  const mod10 = num % 10;
  const mod100 = num % 100;
  if (mod10 === 1 && mod100 !== 11) return `${num} ${one}`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${num} ${few}`;
  return `${num} ${many}`;
};

export const ruRooms = (n) => ruPlural(n, 'комната', 'комнаты', 'комнат');
export const ruSotok = (n) => ruPlural(n, 'сотка', 'сотки', 'соток');
export const ruPrice = (v) => new Intl.NumberFormat('ru-RU').format(v);

// ---- Static SEO texts (Ungheni, Faza 1) ----

// H1 per RU listing page — Ungheni has dedicated texts, the other cities
// and the whole-country pages use the template.
export const ruH1 = (ruBase, saleType, locationSlug) => {
  const exact = RU_H1[`${ruBase}|${saleType}`];
  if (exact && locationSlug === 'ungheni') return exact;
  const label = RU_CATEGORIES.find((c) => c.ru === ruBase)?.label || '';
  const action = RU_ACTION[Number(saleType) === 2 ? 2 : 1];
  const place = locationSlug ? ruCityLoc(locationSlug) : 'Молдове';
  return `${label} ${action} в ${place}`;
};

export const RU_H1 = {
  'kvartiry|1': 'Квартиры на продажу в Унгенах',
  'kvartiry|2': 'Квартиры в аренду в Унгенах',
  'doma|1': 'Дома на продажу в Унгенах',
  'doma|2': 'Дома в аренду в Унгенах',
  'uchastki|1': 'Участки на продажу в Унгенах',
  'uchastki|2': 'Участки в аренду в Унгенах',
  'kommercheskaya-nedvizhimost|1': 'Коммерческая недвижимость на продажу в Унгенах',
  'kommercheskaya-nedvizhimost|2': 'Коммерческая недвижимость в аренду в Унгенах',
};

export const RU_LIST_META = {
  'kvartiry|1': 'Квартиры на продажу в Унгенах: 1, 2, 3 и более комнат. Смотрите актуальные предложения, цены и фотографии недвижимости от LVG Imob.',
  'kvartiry|2': 'Квартиры в аренду в Унгенах. Смотрите доступные предложения, цены, количество комнат и фотографии — свяжитесь с LVG Imob.',
  'doma|1': 'Дома на продажу в Унгенах и окрестностях. Сравните площадь дома, участок, расположение и цену. Смотрите предложения LVG Imob.',
  'doma|2': 'Дома в аренду в Унгенах. Доступные предложения с двором и разной площадью — узнайте условия аренды в LVG Imob.',
  'uchastki|1': 'Участки на продажу в Унгенах и окрестностях для строительства или инвестиций. Смотрите площадь, расположение и цены от LVG Imob.',
  'uchastki|2': 'Участки в аренду в Унгенах. Проверьте площадь, расположение и условия — обращайтесь в LVG Imob.',
  'kommercheskaya-nedvizhimost|1': 'Коммерческая недвижимость на продажу в Унгенах для бизнеса и инвестиций. Смотрите площади, расположение и цены от LVG Imob.',
  'kommercheskaya-nedvizhimost|2': 'Коммерческая недвижимость в аренду в Унгенах для магазинов, офисов и услуг. Сравните площади, районы и цены от LVG Imob.',
};

// DB enum values translated to Russian (values are stored in Romanian).
const RU_VALUE_MAPS = {
  condition: {
    'Locativ': 'Жилое состояние',
    'Varianta sură': 'Серый вариант',
    'Varianta albă': 'Белый вариант',
    'Euroreparație': 'Евроремонт',
    'Reparație de lux': 'Люкс-ремонт',
  },
  construction: {
    'Construcții noi': 'Новостройка',
    'Secundar': 'Вторичный рынок',
  },
  planning: {
    'Seria Stalinka': 'Сталинка',
    'Seria Hrușciovka': 'Хрущёвка',
    'Seria Brejnevka': 'Брежневка',
    'Seria Cehă (Ceșcă)': 'Чешская серия (Чешка)',
    'Seria 102': 'Серия 102',
    'Varnita': 'Варница',
    'Seria 135': 'Серия 135',
    'Seria 143': 'Серия 143',
    'Seria MS': 'Серия МС',
    'Individuală': 'Индивидуальная',
  },
  heating: {
    'Autonomă': 'Автономное',
    'Centralizată': 'Центральное',
  },
  surface: {
    'Teren agricol': 'Сельскохозяйственный участок',
    'Teren pentru construcții': 'Участок под строительство',
  },
};

export const ruValue = (lang, kind, value) => {
  if (lang !== 'ru' || value == null || value === '') return value;
  return RU_VALUE_MAPS[kind]?.[value] || value;
};

export const RU_HOME_META =
  'Недвижимость в Молдове: квартиры, дома, участки и коммерческие помещения. Актуальные предложения LVG Imob в Унгенах, Кишинёве и Бельцах.';
export const RU_CONTACT_META =
  'Свяжитесь с LVG Imob по вопросам покупки, продажи или аренды недвижимости. Унгены, улица Ион Крянгэ 4, тел. +373 690 03 757.';

// Long SEO text per category (structure: intro -> listings -> h2+paragraphs -> CTA).
export const RU_CONTENT = {
  kvartiry: {
    1: {
      intro:
        'Ищете квартиру на продажу в {city}? На LVG Imob собраны актуальные предложения: квартиры с 1, 2, 3 и более комнатами, в новых и вторичных домах. Сравнивайте варианты по площади, этажу, состоянию и цене и записывайтесь на просмотр.',
      h2: 'Как выбрать квартиру в {city}',
      paragraphs: [
        'При выборе квартиры важны не только цена за квадратный метр, но и район, этаж, состояние дома, ремонт и инфраструктура рядом. Квартира в новом доме и жильё на вторичном рынке отличаются по стоимости и возможным расходам после покупки.',
        'LVG Imob помогает сравнивать доступные предложения и подбирать варианты под ваш бюджет. Если объект вам подходит, можно запросить детали и договориться о просмотре.',
        'Если вы планируете покупку в ипотеку, заранее определите бюджет и размер первоначального взноса — так вы быстрее найдёте подходящий вариант.',
      ],
    },
    2: {
      intro:
        'Смотрите квартиры в аренду в {city} — на длительный срок или на условиях, указанных собственником. На LVG Imob можно сравнить актуальные предложения по цене, количеству комнат, району и характеристикам до записи на просмотр.',
      h2: 'Аренда квартиры в {city}: на что обратить внимание',
      paragraphs: [
        'При выборе арендной квартиры месячная цена — не единственный критерий. Имеют значение район, мебель, техника, условия договора, срок аренды и дополнительные расходы.',
        'В этом разделе представлены квартиры, доступные для аренды в {city}. Вы можете изучить фотографии и информацию по каждому объекту и связаться с LVG Imob для уточнения деталей.',
        'Предложения аренды меняются быстро. Если квартира вам подходит, рекомендуем сразу проверить её доступность и записаться на просмотр.',
      ],
    },
  },
  doma: {
    1: {
      intro:
        'Дома на продажу в {city} и окрестностях: сравнивайте предложения по площади дома, участку, расположению, состоянию и цене. LVG Imob публикует объекты для разных задач — от домов под ремонт до жилья, готового к переезду.',
      h2: 'Как выбрать дом в {city}',
      paragraphs: [
        'Покупка дома требует больше проверок, чем выбор квартиры. Кроме площади и количества комнат стоит оценить участок, подъезд, коммуникации, состояние постройки и возможные расходы на ремонт.',
        'Расположение не менее важно: для одних решающими являются близость к центру, школам и магазинам, для других — большой участок, тишина или удобный выезд на основные дороги.',
        'На LVG Imob вы можете посмотреть дома, доступные в {city}, и запросить дополнительную информацию или просмотр понравившегося объекта.',
      ],
    },
    2: {
      intro:
        'Ищете дом в аренду в {city}? Ознакомьтесь с доступными предложениями: площадь дома, количество комнат, двор, оснащение, район и условия аренды. LVG Imob поможет подобрать вариант под ваши потребности и бюджет.',
      h2: 'Аренда дома в {city}',
      paragraphs: [
        'Дом в аренду подходит семьям, тем, кому нужно больше пространства, или тем, кто предпочитает собственный двор вместо квартиры.',
        'До принятия решения уточните, что входит в аренду и какие расходы остаются за арендатором: отопление, коммунальные услуги, мебель, техника, парковка и порядок использования двора.',
        'На LVG Imob публикуются дома, доступные для аренды в {city}, когда такие предложения есть. По каждому объявлению можно запросить детали или просмотр.',
      ],
    },
  },
  uchastki: {
    1: {
      intro:
        'Участки на продажу в {city} и окрестностях — для строительства, инвестиций или других целей, указанных в объявлении. Сравнивайте предложения по площади, расположению, подъезду, коммуникациям и цене.',
      h2: 'Что проверить перед покупкой участка',
      paragraphs: [
        'Цена и площадь — не единственное, что определяет, подходит ли участок для вашего проекта. Важнее всего назначение земли и то, что на ней можно построить законно.',
        'Учитывайте подъезд к участку, форму надела, рельеф и наличие коммуникаций. Подключение к электричеству, воде, канализации или газу может заметно повлиять на общий бюджет строительства.',
        'На LVG Imob вы можете изучить участки, доступные в {city}, и запросить детали по каждому объекту до принятия решения.',
      ],
    },
    2: {
      intro:
        'Участки в аренду в {city}: информация о площади, расположении, подъезде и условиях использования. В зависимости от объекта и разрешённого назначения участок может подойти для коммерческой деятельности, хранения или других целей по договорённости с собственником.',
      h2: 'Аренда участка в {city}',
      paragraphs: [
        'До аренды определите, для чего будет использоваться участок: требования к хранению, коммерческой деятельности или временному использованию сильно различаются.',
        'Проверьте автомобильный подъезд, полезную площадь, наличие электричества и условия, установленные собственником.',
        'На LVG Imob публикуются участки, доступные для аренды в {city}, когда такие предложения есть.',
      ],
    },
  },
  'kommercheskaya-nedvizhimost': {
    1: {
      intro:
        'Ищете коммерческое помещение на продажу в {city}? На LVG Imob собраны объекты для бизнеса и инвестиций: сравнивайте предложения по площади, расположению, подъезду, планировке и цене.',
      h2: 'Коммерческие помещения для бизнеса и инвестиций в {city}',
      paragraphs: [
        'Коммерческое помещение нужно оценивать в первую очередь исходя из вида деятельности. Для магазина важны видимость и пешеходный трафик, для офиса — доступность, парковка и планировка.',
        'Если помещение покупается как инвестиция, оцените потенциал аренды: расположение, состояние объекта и спрос в этом районе влияют и на ставку аренды, и на время поиска арендатора.',
        'На LVG Imob публикуются коммерческие помещения в {city} для разных видов деятельности и бюджетов. По каждому объекту можно запросить детали или просмотр.',
      ],
    },
    2: {
      intro:
        'Коммерческие помещения в аренду в {city} — для магазина, офиса, сферы услуг и других видов деятельности. Сравнивайте предложения по площади, району, видимости, подъезду и цене.',
      h2: 'Выберите помещение для вашего бизнеса',
      paragraphs: [
        'Для коммерческого помещения расположение напрямую влияет на бизнес: магазину или шоуруму нужны видимость и пешеходный трафик, офису — доступность, комфорт и парковка.',
        'До подписания договора проверьте полезную площадь, планировку, подъезд, коммуникации и возможные дополнительные расходы.',
        'На LVG Imob вы можете изучить коммерческие помещения, доступные для аренды в {city}, и запросить информацию по интересующим объектам.',
      ],
    },
  },
};

// Short SEO content for garages / other properties (used on RU pages).
RU_CONTENT.garazhi = {
  1: {
    intro: 'Гаражи на продажу в {city}: сравнивайте предложения по площади, расположению, подъезду и цене. На LVG Imob можно посмотреть информацию по каждому объекту и запросить дополнительные детали.',
    h2: 'Как выбрать гараж в {city}',
    paragraphs: [
      'Перед покупкой проверьте площадь, автомобильный подъезд, состояние постройки и расположение гаража. Важна и близость к дому или месту работы.',
      'На LVG Imob вы можете посмотреть гаражи, доступные в {city}, и запросить информацию по интересующим объектам.',
    ],
  },
  2: {
    intro: 'Гаражи в аренду в {city}: информация о площади, подъезде и условиях использования. Обращайтесь в LVG Imob за деталями.',
    h2: 'Аренда гаража в {city}',
    paragraphs: [
      'Перед арендой проверьте площадь, автомобильный подъезд, безопасность и условия, установленные собственником.',
    ],
  },
};
RU_CONTENT.prochee = {
  1: {
    intro: 'Другая недвижимость на продажу в {city}: сравнивайте предложения по характеристикам, расположению и цене. По каждому объекту можно запросить дополнительные детали.',
    h2: 'Различная недвижимость на продажу в {city}',
    paragraphs: [
      'В категории «Прочее» собраны объекты разного назначения. Ознакомьтесь с описанием каждого объявления, чтобы понять, что включает объект и как его можно использовать.',
      'На LVG Imob вы можете посмотреть доступные объекты в {city} и связаться с командой за деталями.',
    ],
  },
  2: {
    intro: 'Другая недвижимость в аренду в {city}: узнайте условия использования по каждому предложению. Обращайтесь в LVG Imob за деталями.',
    h2: 'Различная недвижимость в аренду в {city}',
    paragraphs: [
      'Ознакомьтесь с описанием каждого объявления, чтобы понять назначение объекта и условия аренды, установленные собственником.',
    ],
  },
};

export function getRuContent(ruBase, saleType, city) {
  const forBase = RU_CONTENT[ruBase];
  if (!forBase) return null;
  const c = forBase[Number(saleType) === 2 ? 2 : 1];
  if (!c) return null;
  const fill = (text) => text.replaceAll('{city}', city);
  return {
    intro: fill(c.intro),
    h2: fill(c.h2),
    paragraphs: c.paragraphs.map(fill),
  };
}

// ---- JSON-LD (Russian) ----

export function ruItemListSchema(ruBase, locSlug, results) {
  if (!results || !results.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: results.map((item, idx) => {
      const l = item.listing || {};
      const ruSlug = l.ru_slug || l.slug;
      return {
        '@type': 'ListItem',
        position: idx + 1,
        name: l.ru_seo_title || l.seo_title || l.ru_title || l.street,
        url: ruSlug
          ? `${SITE_URL}/ru/${ruBase}/${locSlug}/${ruSlug}`
          : `${SITE_URL}/ru/${ruBase}`,
      };
    }),
  };
}

// RU variant of RealEstateListing — points at the RU canonical URL and uses
// the translated content.
export function ruRealEstateListingSchema(ruBase, locSlug, listing) {
  const l = listing.listing || {};
  const ruSlug = l.ru_slug || l.slug;
  const canonicalUrl = `${SITE_URL}/ru/${ruBase}/${locSlug}/${ruSlug}`;
  const aboutType = ruBase === 'kvartiry' ? 'Apartment' : ruBase === 'doma' ? 'House' : 'Place';

  const floorSize = {};
  const sizeValue = listing.surface ?? listing.land_surface;
  if (sizeValue != null && ruBase !== 'uchastki') {
    floorSize.floorSize = {
      '@type': 'QuantitativeValue',
      value: sizeValue,
      unitCode: 'MTK',
    };
  }
  const rooms = listing.rooms != null ? { numberOfRooms: listing.rooms } : {};
  const name = l.ru_seo_title || l.seo_title || l.ru_title || l.street;

  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    '@id': `${canonicalUrl}#listing`,
    url: canonicalUrl,
    name,
    description: (l.ru_meta_description || '').slice(0, 300),
    datePosted: (l.created_at || '').slice(0, 10),
    about: {
      '@type': aboutType,
      name,
      ...rooms,
      ...floorSize,
      address: {
        '@type': 'PostalAddress',
        addressLocality: l.location?.location || '',
        addressCountry: 'MD',
      },
    },
    offers: {
      '@type': 'Offer',
      price: String(l.price ?? ''),
      priceCurrency: 'EUR',
      url: canonicalUrl,
      seller: { '@id': ORG_ID },
    },
  };
}

// Hreflang triple for RU pages: ro (RO equivalent) + ru (this page) + x-default.
export function ruHreflang(roUrlValue, ruUrlValue) {
  return [
    { rel: 'alternate', hreflang: 'ro', href: roUrlValue },
    { rel: 'alternate', hreflang: 'ru', href: ruUrlValue },
    { rel: 'alternate', hreflang: 'x-default', href: roUrlValue },
  ];
}

// ---- Language-aware path helpers (same components for RO + RU) ----

import { buildListPath } from './urls';

// Listing page path for the active language.
export const listPathLang = (lang, base, { location = '', sector = '', saleType = 1 }) =>
  lang === 'ru'
    ? ruListPath(RU_BASE_MAP[base] || base, location || 'ungheni', saleType)
    : buildListPath(base, { location, sector, saleType });

// Detail page path for the active language (uses ru_slug under /ru/).
export const detailPathLang = (lang, base, locSlug, slug, ruSlug) =>
  lang === 'ru'
    ? ruDetailPath(RU_BASE_MAP[base] || base, locSlug, ruSlug || slug)
    : `/${base}/${locSlug}/${slug}`;

export const homePath = (lang) => (lang === 'ru' ? '/ru/' : '/');

// Language-switch link: jumps to the SAME page in the other language.
// Detail pages fall back on the API accepting either slug, so switching
// with the "wrong" slug still lands on the right property (301 to canonical).
export const switchLangPath = (pathname) => {
  const isRu = pathname.startsWith('/ru');
  const segs = pathname.split('/').filter(Boolean);
  const CITIES = ['ungheni', 'chisinau', 'balti'];

  if (isRu) {
    const rest = segs.slice(1);
    // static RU pages
    if (rest.length === 1 && rest[0] === 'o-nas') return '/despre-noi';
    // /ru/{ruBase}/{prodazha|arenda} (whole country)
    if (rest.length === 2 && (rest[1] === 'prodazha' || rest[1] === 'arenda')) {
      const roBase = RO_BASE_MAP[rest[0]];
      if (roBase) return rest[1] === 'arenda' ? `/${roBase}/chirie` : `/${roBase}`;
      return '/';
    }
    const [ruBase, loc, ...more] = rest;
    const roBase = RO_BASE_MAP[ruBase];
    if (roBase && CITIES.includes(loc) && more.length) {
      if (more[0] === 'prodazha') return `/${roBase}/${loc}`;
      if (more[0] === 'arenda') return `/${roBase}/${loc}/chirie`;
      return `/${roBase}/${loc}/${more[0]}`;
    }
    return '/';
  }

  const [base, loc, ...more] = segs;
  // static RO pages
  if (segs.length === 1 && base === 'despre-noi') return '/ru/o-nas';
  const ruBase = RU_BASE_MAP[base];
  // whole-country RO category pages -> whole-country RU pages
  if (ruBase && (loc === undefined || loc === 'chirie')) {
    if (loc === 'chirie') return ruListPath(ruBase, '', 2);
    return ruListPath(ruBase, '', 1);
  }
  if (ruBase && CITIES.includes(loc)) {
    if (more.length === 0) return ruListPath(ruBase, loc, 1);
    if (more[0] === 'chirie') return ruListPath(ruBase, loc, 2);
    if (more[0] === 'vanzare') return ruListPath(ruBase, loc, 1);
    if (/^[1-9]-camer[ae]$/.test(more[0])) return ruListPath(ruBase, loc, 1);
    // detail (slug sau id) — backend-ul RU acceptă ambele slug-uri
    return ruDetailPath(ruBase, loc, more[0]);
  }
  return '/ru/';
};
export const contactPath = (lang) => (lang === 'ru' ? '/ru/contacte' : '/contacte');
export const aboutPath = (lang) => (lang === 'ru' ? '/ru/o-nas' : '/despre-noi');

// RO path -> RU path for pages that exist in both languages (Faza 1).
const RO_TO_RU_PATH = {
  '/': '/ru/',
  '/despre-noi': '/ru/o-nas',
  '/contacte': '/ru/contacte',
  '/apartamente/ungheni': '/ru/kvartiry/ungheni/prodazha',
  '/apartamente/ungheni/chirie': '/ru/kvartiry/ungheni/arenda',
  '/case/ungheni': '/ru/doma/ungheni/prodazha',
  '/case/ungheni/chirie': '/ru/doma/ungheni/arenda',
  '/terenuri/ungheni': '/ru/uchastki/ungheni/prodazha',
  '/terenuri/ungheni/chirie': '/ru/uchastki/ungheni/arenda',
  '/spatii-comerciale/ungheni': '/ru/kommercheskaya-nedvizhimost/ungheni/prodazha',
  '/spatii-comerciale/ungheni/chirie': '/ru/kommercheskaya-nedvizhimost/ungheni/arenda',
};

// Maps a path into the active language when an equivalent exists.
export const pathLang = (lang, path) => {
  if (lang !== 'ru') return path;
  return RO_TO_RU_PATH[path] || path;
};

// Nav/dropdown links per language (Ungheni pages exist in RU; the rest fall
// back to the Romanian pages until Faza 2).
export const NAV_CATEGORIES = [
  { label: 'Apartamente', ruLabel: 'Квартиры', base: 'apartamente' },
  { label: 'Case', ruLabel: 'Дома', base: 'case' },
  { label: 'Terenuri', ruLabel: 'Участки', base: 'terenuri' },
  { label: 'Spații comerciale', ruLabel: 'Коммерческая недвижимость', base: 'spatii-comerciale' },
  { label: 'Garaje', ruLabel: 'Гаражи', base: 'garajuri' },
  { label: 'Altele', ruLabel: 'Прочее', base: 'altele' },
];

export const navCategoryPath = (lang, base, saleType) =>
  lang === 'ru'
    ? ruListPath(RU_BASE_MAP[base] || base, '', saleType)
    : buildListPath(base, { location: '', sector: '', saleType });

// Promo ticker text per language.
export const PROMO_TEXT = {
  ro: {
    full: 'Vrei să cumperi un imobil prin credit ipotecar? Aplică prin recomandarea LVG Imob și îți rambursăm costul evaluării imobilului. Află mai multe →',
    highlight: 'îți rambursăm costul evaluării imobilului',
  },
  ru: {
    full: 'Хотите купить недвижимость в ипотеку? Оформите кредит по рекомендации LVG Imob — мы компенсируем стоимость оценки недвижимости. Узнать больше →',
    highlight: 'мы компенсируем стоимость оценки недвижимости',
  },
};
